import React, { useEffect, useState } from "react";
import { Copy, Check, ExternalLink, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "@/lib/share";
import PostCalendar from "@/components/admin/PostCalendar";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = "https://mondearabe.org";
const LINKS = [
  ["Norme sulla privacy (Play Console → Norme dell'app)", `${SITE}/privacy`],
  ["Regole della community (sezione Contenuti generati dagli utenti)", `${SITE}/regole`],
  ["Eliminazione dati/account (Sicurezza dei dati → URL eliminazione)", `${SITE}/elimina-dati`],
  ["Digital Asset Links (verifica dominio TWA)", `${SITE}/.well-known/assetlinks.json`],
  ["Sito web dell'app", SITE],
  ["Email di contatto sviluppatore", "test.mondearabe2026@gmail.com"],
];
const REMINDERS = [
  "Pubblico di destinazione: scegli SOLO 13-15, 16-17 e 18+ (mai 'bambini'): con annunci AdSense e contenuti utente l'app non può rientrare nel programma Famiglie.",
  "Contenuti dell'app → Contenuti generati dagli utenti: SÌ · moderazione umana prima della pubblicazione · segnalazione in-app (pulsante Segnala) · blocco (Nascondi autore) · regole accettate con checkbox.",
  "Contenuti dell'app → Annunci: SÌ (AdSense). Classificazione contenuti: compila il questionario IARC (nessuna violenza, UGC moderati).",
  "Sicurezza dei dati: copia le righe della tabella qui sotto. Posizione = NON raccolta (elaborata solo sul dispositivo su richiesta).",
  "Google Maps: la Maps Embed API deve risultare 'Abilitata' sulla chiave, altrimenti la scheda Mappa mostra l'elenco tappe (già gestito).",
  "Account personale: servono 12 tester iscritti al test chiuso per 14 giorni consecutivi prima di richiedere la produzione.",
  "Acquisti in-app: gli e-book usano Google Play Billing nell'app Android (Stripe solo sul web). Crea i prodotti con gli stessi ID usati nel codice.",
];

const parseListing = (md) => md.split(/\n## /).slice(1).map((sec) => {
  const [head, ...rest] = sec.split("\n");
  const fields = [];
  rest.join("\n").split(/\n\*\*(.+?)\*\*\n/).slice(1).forEach((v, i, arr) => { if (i % 2 === 0) fields.push({ label: v, text: (arr[i + 1] || "").replace(/\n---\s*$/, "").trim() }); });
  return { lang: head.trim(), fields };
});
const parseSafety = (md) => md.split(/\n## /).slice(1).map((sec) => { const [head, ...rest] = sec.split("\n"); return { title: head.trim(), text: rest.join("\n").trim() }; });

function CopyBtn({ text, id }) {
  const [ok, setOk] = useState(false);
  const run = async () => { await copyText(text); setOk(true); toast.success("Copiato"); setTimeout(() => setOk(false), 1500); };
  return <button onClick={run} className="inline-flex items-center gap-1 rounded-full border border-stoneLine px-2.5 py-1 text-[11px] font-semibold hover:bg-sandDeep/50 shrink-0" data-testid={`copy-${id}`}>{ok ? <Check className="w-3 h-3 text-oasis" /> : <Copy className="w-3 h-3" />} Copia</button>;
}

function Block({ title, text, id, limit }) {
  const over = limit && text.length > limit;
  return (
    <div className="rounded-2xl border border-stoneLine bg-card p-3" data-testid={`play-block-${id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5"><span className="text-xs font-semibold text-ink">{title}{limit ? <span className={`ms-2 stat-number ${over ? "text-red-600" : "text-muted-foreground"}`}>{text.length}/{limit}</span> : null}</span><CopyBtn text={text} id={id} /></div>
      <pre className="whitespace-pre-wrap font-sans text-xs text-ink/85 max-h-48 overflow-auto">{text}</pre>
    </div>
  );
}

export default function PlayChecklist() {
  const [data, setData] = useState(null);
  const [banners, setBanners] = useState({ files: [], zip: null });
  useEffect(() => {
    fetch(`${API}/api/admin/play-checklist`, { credentials: "include" }).then((r) => r.json()).then(setData).catch(() => setData({ listing: "", data_safety: "" }));
    fetch(`${API}/api/admin/social-banners`, { credentials: "include" }).then((r) => r.json()).then(setBanners).catch(() => {});
  }, []);
  const limits = { 0: 30, 1: 80, 2: 4000 };
  const byCountry = banners.files.reduce((acc, f) => { const c = f.split("-")[0]; (acc[c] ||= []).push(f); return acc; }, {});
  return (
    <div className="space-y-8" data-testid="play-checklist">
      <section data-testid="social-banners">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="font-display font-bold text-lg text-ink">0 · Banner social per paese (Facebook 1200×628 · Instagram 1080×1080 · Story/Reel 1080×1920 · IT/EN/FR/AR)</h2>
          {banners.zip && <a href={`${API}/api/static/banners/social/${banners.zip}`} download className="inline-flex items-center gap-1.5 rounded-full bg-ink text-sand px-4 py-2 text-sm font-semibold hover:bg-ochre" data-testid="social-zip"><Download className="w-4 h-4" /> Scarica tutti (zip)</a>}
        </div>
        {!banners.files.length && <p className="text-sm text-muted-foreground">Nessun banner ancora generato.</p>}
        {Object.entries(byCountry).map(([code, files]) => (
          <div key={code} className="mb-4" data-testid={`social-country-${code}`}>
            <h3 className="text-xs font-semibold text-ochre uppercase tracking-wider mb-2">{code}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {files.map((f) => (
                <a key={f} href={`${API}/api/static/banners/social/${f}`} download target="_blank" rel="noopener noreferrer" className="group rounded-xl overflow-hidden border border-stoneLine bg-card hover:border-ochre" data-testid={`social-file-${f.replace(".png", "")}`}>
                  <img src={`${API}/api/static/banners/social/${f}`} alt={f} loading="lazy" className="w-full aspect-[1.6] object-cover group-hover:opacity-90" />
                  <div className="px-2 py-1 text-[11px] text-ink/80 flex items-center justify-between">{f.replace(".png", "").replace(`${code}-`, "").replace(/^(fb|ig|st)-/, (m, k) => `${{ fb: "Facebook", ig: "Instagram", st: "Story" }[k]}-`).toUpperCase()} <Download className="w-3 h-3" /></div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </section>
      <PostCalendar />
      <section>
        <h2 className="font-display font-bold text-lg text-ink mb-3">1 · Link da incollare</h2>
        <ul className="space-y-2">{LINKS.map(([label, url], i) => (
          <li key={url} className="flex items-center gap-2 rounded-2xl border border-stoneLine bg-card px-3 py-2" data-testid={`play-link-${i}`}>
            <div className="flex-1 min-w-0"><div className="text-[11px] text-muted-foreground">{label}</div><a href={url.startsWith("http") ? url : `mailto:${url}`} target="_blank" rel="noopener noreferrer" className="text-sm text-ochre hover:underline break-all inline-flex items-center gap-1">{url} <ExternalLink className="w-3 h-3" /></a></div>
            <CopyBtn text={url} id={`link-${i}`} />
          </li>))}</ul>
      </section>
      <section>
        <h2 className="font-display font-bold text-lg text-ink mb-3">2 · Promemoria prima di inviare</h2>
        <ul className="space-y-2">{REMINDERS.map((r, i) => <li key={i} className="flex gap-2 text-sm text-ink/85 rounded-2xl bg-[#FFF4E5] border border-[#F3D9A4] px-3 py-2" data-testid={`play-reminder-${i}`}><AlertTriangle className="w-4 h-4 text-ochre shrink-0 mt-0.5" /><span>{r}</span></li>)}</ul>
      </section>
      <section>
        <h2 className="font-display font-bold text-lg text-ink mb-3">3 · Testi della scheda (titolo ≤30 · breve ≤80 · completa ≤4000)</h2>
        {!data && <p className="text-sm text-muted-foreground">…</p>}
        {data && parseListing(data.listing).map((sec, si) => (
          <div key={sec.lang} className="mb-4" data-testid={`play-listing-${si}`}>
            <h3 className="text-sm font-semibold text-ochre uppercase tracking-wider mb-2">{sec.lang}</h3>
            <div className="grid gap-2">{sec.fields.map((f, fi) => <Block key={f.label} title={f.label} text={f.text} id={`${si}-${fi}`} limit={limits[fi]} />)}</div>
          </div>))}
      </section>
      <section>
        <h2 className="font-display font-bold text-lg text-ink mb-3">4 · Sicurezza dei dati (Data Safety) — risposte riga per riga</h2>
        {data && parseSafety(data.data_safety).map((sec, i) => <div key={sec.title} className="mb-2"><Block title={sec.title} text={sec.text} id={`safety-${i}`} /></div>)}
      </section>
    </div>
  );
}
